// src/components/ui/WizardModal.jsx
//
// The wide modal dialog that hosts ONE step of a process at a time (v198, first used by Buyer Personas).
// Header (fixed): title, optional subtitle, optional StepBar (hidden for fewer than 2 steps), the ×.
// Body: scrolls. Footer (fixed): filled by the BODY through <ModalFooter>, which portals its children into the
// footer slot — so the step that owns the state also owns its buttons, and Salva / Avanti never scroll away.
//
// ⛔ Not AdminModal: that one is used by ~12 screens (every ConfirmDialog included) and scrolls as a whole.
// Shared with it: modalStack.js — Escape and the Tab trap act only while THIS modal is the top-most one, so a
// nested ConfirmDialog (rendered inside the body, e.g. the document picker's replace confirmation, or our own
// "close without saving?") owns Escape while it is open.
//
// Closing is always a REQUEST (`onRequestClose`): ×, Escape and a mousedown on the overlay all call it, and the
// owner decides (e.g. asks about unsaved changes). Focus moves into the dialog on open and back to whatever had
// it on close. Portalled to document.body; React events still bubble through the React tree.
import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';
import { useModalLayer } from './modalStack.js';
import { useContentInset } from './modalArea.js';
import StepBar from './StepBar.jsx';

// `inModal: false` (the default, no WizardModal above) → ModalFooter renders its children IN PLACE, so a step
// body stays testable and usable on its own. Inside a modal it waits for the slot element.
const FooterSlot = createContext({ inModal: false, el: null });

export function ModalFooter({ children }) {
  const { inModal, el } = useContext(FooterSlot);
  if (!inModal) return <div style={INLINE_FOOTER}>{children}</div>;
  return el ? createPortal(children, el) : null;
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'textarea:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

// Same overlay tint as AdminModal (kept literal there too).
const OVERLAY = {
  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: FONT_SANS,
};
const panel = (width) => ({
  background: COLORS.surface, borderRadius: RADII.control, width: '100%', maxWidth: width,
  maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column',
  boxShadow: '0 8px 40px rgba(0,0,0,0.15)', outline: 'none',
});
const HEADER = { flexShrink: 0, padding: '20px 24px 16px', borderBottom: `1px solid ${COLORS.border}` };
const HEAD_ROW = { display: 'flex', alignItems: 'flex-start', gap: '12px' };
const TITLE = { flex: 1, margin: 0, fontSize: '18px', fontWeight: 700, color: COLORS.ink };
const SUBTITLE = { margin: '4px 0 0', fontSize: '12px', color: COLORS.textTertiary };
// With an `icon`: the add-user dialog's header (Gestione Workspace) — a round tile, then title over subtitle.
const ICON_TILE = {
  position: 'relative', width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
  border: `1px solid ${COLORS.border}`, color: COLORS.ink,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const TITLE_WITH_ICON = { margin: 0, fontSize: '16px', fontWeight: 700, lineHeight: '24px', color: COLORS.ink };
const SUBTITLE_WITH_ICON = { margin: 0, fontSize: '12px', color: COLORS.textTertiary };
const CLOSE = {
  background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textSecondary, padding: '4px',
};
const BODY = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px 24px' };
const FOOTER = { flexShrink: 0, padding: '14px 24px', borderTop: `1px solid ${COLORS.border}` };
const INLINE_FOOTER = { marginTop: '20px' };

export default function WizardModal({
  open, title, subtitle, icon, steps, current, stepsLabel, closeLabel, onRequestClose, width = 880, children,
}) {
  const titleId = useId();
  const isTop = useModalLayer(open);
  const panelRef = useRef(null);
  const [footerEl, setFooterEl] = useState(null);
  const insetLeft = useContentInset(open);
  const requestRef = useRef(onRequestClose);
  requestRef.current = onRequestClose;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    if (panelRef.current) panelRef.current.focus();
    return () => {
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (requestRef.current) requestRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (items.length === 0) { e.preventDefault(); panelRef.current.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isTop]);

  if (!open) return null;
  return createPortal(
    <div
      style={insetLeft ? { ...OVERLAY, paddingLeft: `${insetLeft + 16}px` } : OVERLAY}
      data-testid="wizard-modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && requestRef.current) requestRef.current(); }}
    >
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} style={panel(width)}>
        <header style={HEADER}>
          <div style={icon ? { ...HEAD_ROW, alignItems: 'center' } : HEAD_ROW}>
            {icon ? (
              <>
                <div style={ICON_TILE} aria-hidden="true">{icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 id={titleId} style={TITLE_WITH_ICON}>{title}</h2>
                  {subtitle && <p style={SUBTITLE_WITH_ICON}>{subtitle}</p>}
                </div>
              </>
            ) : (
              <h2 id={titleId} style={TITLE}>{title}</h2>
            )}
            <button
              type="button" aria-label={closeLabel} title={closeLabel} style={CLOSE}
              onClick={() => { if (requestRef.current) requestRef.current(); }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          {!icon && subtitle && <p style={SUBTITLE}>{subtitle}</p>}
          {Array.isArray(steps) && steps.length > 1 && (
            <StepBar steps={steps} current={current} label={stepsLabel} />
          )}
        </header>
        <FooterSlot.Provider value={{ inModal: true, el: footerEl }}>
          <div style={BODY}>{children}</div>
        </FooterSlot.Provider>
        <footer ref={setFooterEl} style={FOOTER} />
      </div>
    </div>,
    document.body,
  );
}
